import MySkeleton from "../../components/skeleton/my/mySkeleton";
import { MyBeforeLogin } from "./ui/myBeforeLogin";
import { TopBar } from "../../widgets";
import { IssuedWellnessTicketCarousel, ReservatedProgramList } from "../../features";
import { GnbFooter, MemberNameAndTitle, MemberBenefitInfo, BasicInfoRow } from "../../shared";
import { removeUser, getPremiumMembership, getDosanProgramMember, getMemberByMemberId, getAllReservationByMemberIdAndReservationStatus } from "../../service";
import { isLogin, moneyStringFormatByNumber, isPlatformTeamMember } from "../../utils";
import { Button, Divider } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllIssuedWellnessTicketByMemberId } from "../../service/issuedWellnessTicket";
import { useQuery } from "@tanstack/react-query";
import { useGetProgramBookingListWebByStatus } from "./model";
import KakaoInquiry from "./ui/kakaoInquiry";
import { useMyContext } from "../../entities/context";

const fetchIssuedWellnessTicket = () => getAllIssuedWellnessTicketByMemberId().then(res => res.data);
const fetchReservationList = () => getAllReservationByMemberIdAndReservationStatus('RESERVATION').then(res => res.data);
const fetchMemberData = () => getMemberByMemberId().then(res => res.data);
const fetchDosanProgramMember = () => getDosanProgramMember().then(res => res.data);
const fetchPremiumMembership = () => getPremiumMembership().then(res => res.data);

const My = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const focusIssuedWellnessTicketId = (location.state as { focusIssuedWellnessTicketId?: number } | null)?.focusIssuedWellnessTicketId;
    const { setMyReservationHistoryTabKey } = useMyContext();
    let isInitLoading = false;
    const { data: myWellnessTicketList, isFetching: isLoadingTickets, isError: isErrorTickets, error: errorTickets } = useQuery({ queryKey: ['issuedWellnessTicket'], queryFn: fetchIssuedWellnessTicket, select: (res) => res, enabled: isLogin() });
    const { data: myReservationList, isFetching: isLoadingReservations, isError: isErrorReservations, error: errorReservations } = useQuery({ queryKey: ['reservationList'], queryFn: fetchReservationList, enabled: isLogin() });
    const { data: programBookingList, isFetching } = useGetProgramBookingListWebByStatus("COMPLETE", isLogin(), 'programBookingId programThumbnailImageUrl programTitle targetDateTime programLocationName')
    const { data: member, isFetching: isLoadingMember, isError: isErrorMember, error: errorMember } = useQuery({ queryKey: ['member'], queryFn: fetchMemberData, enabled: isLogin() });
    const { data: dosanProgramMember, isFetching: isLoadingDosan, isError: isErrorDosan, error: errorDosan } = useQuery({ queryKey: ['dosanProgramMember'], queryFn: fetchDosanProgramMember, enabled: isLogin() });
    const { data: premiumMembership, isFetching: isLoadingMembership, isError: isErrorMembership, error: errorMembership } = useQuery({ queryKey: ['premiumMembership'], queryFn: fetchPremiumMembership, enabled: isLogin() });

    if (isLogin()) {
        isInitLoading = isLoadingMember || isLoadingTickets || isLoadingReservations || isLoadingDosan || isLoadingMembership;

        if (isInitLoading) {
            return <>
                <MySkeleton />
                <GnbFooter activeButton='my' />
            </>
        }

        if (isErrorMember || isErrorTickets || isErrorReservations || isErrorDosan || isErrorMembership) {
            console.error('Error fetching data:', errorMember, errorTickets, errorReservations, errorDosan, errorMembership);
            removeUser();
        }
    } else {
        isInitLoading = false
        return <>
            <TopBar title="MY" leftIcon={false} rightIcon={false} position='fixed' thema='dark' />
            <MyBeforeLogin />
            <KakaoInquiry />
            <GnbFooter activeButton='my' />
        </>
    }

    return <>
        <div>
            <>
                {member && <>
                    <TopBar title="MY" leftIcon={false} rightType='setting' position='fixed' thema='dark' />
                    <div style={{ paddingInline: 'var(--space20)', marginTop: 24, marginBottom: 40 }}>
                        <MemberNameAndTitle
                            name={member.name}
                            title={member.isManager ? "매니저님" : member.isTeacher ? '코치님' : '회원님'} />
                        <div style={{ display: 'flex', gap: 'var(--space16)', marginBlock: '24px' }}>
                            <div style={{ width: '50%' }}>
                                <MemberBenefitInfo title="멤버십" value={premiumMembership ? premiumMembership.membershipTitle : member.grade} to={"/member-ship"} styles={{ icon: { margin: 0 } }} />
                            </div>
                            <div style={{ width: '50%' }}>
                                <MemberBenefitInfo title="포인트 · 쿠폰" value={moneyStringFormatByNumber(member.totalPoint) + "p"} to="/my/reward" styles={{ icon: { margin: 0 } }} />
                            </div>
                        </div>
                    </div>

                    {myReservationList && programBookingList &&
                        <ReservatedProgramList
                            dosanProgramMember={dosanProgramMember}
                            myReservationList={myReservationList}
                            programBookingList={programBookingList} />}

                    {myWellnessTicketList && <IssuedWellnessTicketCarousel myIssuedWellnessTicketList={myWellnessTicketList} focusIssuedWellnessTicketId={focusIssuedWellnessTicketId} />}

                    <div style={{ paddingInline: 20, marginTop: 32 }}>
                        {member.isTeacher && <>
                            <BasicInfoRow title={member.isManager ? "모든 수업 확인" : "내 수업 확인"} onClick={() => navigate(`/mylecture/teacher/${member.teacherId}`)} styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }} />
                            <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                        </>}
                        <BasicInfoRow title={"예약 내역"} onClick={() => { setMyReservationHistoryTabKey(0); navigate("/my/reservation-history/" + member.id) }} styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }} />
                        <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                        <BasicInfoRow title={"구매 내역"} onClick={() => navigate("/my/paid-history/" + member.id)} styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }} />
                        <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                        <BasicInfoRow title={"정기권 구매"} onClick={() => navigate("/wellness-ticket-group")} styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }} />
                        <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                        <BasicInfoRow title={"공지사항"} onClick={() => navigate("/notice")} styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }} />

                        {(member.isManager || isPlatformTeamMember(member.mobile)) &&
                            <>
                                <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                                <BasicInfoRow title={"이벤트"} onClick={() => navigate("/event")} styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }} />
                            </>}
                        {isPlatformTeamMember(member.mobile) &&
                            <>
                                <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                                <BasicInfoRow
                                    styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }}
                                    title={window.location.origin === "https://dev.positivehotelyoga.com" ? "운영용 페이지로 이동" : "개발용 페이지로 이동"}
                                    onClick={() => window.location.href = window.location.origin === "https://dev.positivehotelyoga.com" ? "https://positivehotelyoga.com" : "https://dev.positivehotelyoga.com"} />
                                {member.mobile === '01058513100' &&
                                    <>
                                        <Button onClick={() => navigate('/community')}>커뮤니티</Button>
                                        <Divider style={{ borderBlockStart: '1px solid var(--secondarys100)', margin: '0' }} />
                                        <BasicInfoRow
                                            styles={{ container: { padding: '12px 0px' }, arrowRight: { padding: 8 } }}
                                            title={window.location.origin === "https://dev.positivehotelyoga.com" ? "로컬 페이지로 이동" : "개발용 페이지로 이동"}
                                            onClick={() => window.location.href = window.location.origin === "https://dev.positivehotelyoga.com" ? "http://localhost:3000" : "https://dev.positivehotelyoga.com"} />
                                    </>}
                            </>}
                    </div>
                </>}
            </>
        </div>
        <KakaoInquiry />
        <GnbFooter activeButton='my' />
    </>
}

export { My }
